import React from 'react'
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  useWatch,
} from 'react-hook-form'
import {getFieldError} from './getFieldError'

type MonthYearInputProps<T extends FieldValues> = {
  name: Path<T>
  monthUnknownName?: Path<T>
  monthUnknownLabel?: string
  label?: string
  helperText?: string
  control: Control<T>
  errors?: FieldErrors<T>
  required?: boolean | string
  className?: string
  inputClassName?: string
  layout?: 'stacked' | 'half'
  options?: Omit<RegisterOptions<T, Path<T>>, 'setValueAs' | 'valueAsNumber' | 'valueAsDate'>
}

const MONTHS = [
  {value: '01', label: 'January'},
  {value: '02', label: 'February'},
  {value: '03', label: 'March'},
  {value: '04', label: 'April'},
  {value: '05', label: 'May'},
  {value: '06', label: 'June'},
  {value: '07', label: 'July'},
  {value: '08', label: 'August'},
  {value: '09', label: 'September'},
  {value: '10', label: 'October'},
  {value: '11', label: 'November'},
  {value: '12', label: 'December'},
]

const UNKNOWN_MONTH_VALUE = 'unknown'

const CURRENT_YEAR = new Date().getFullYear()

const YEARS = Array.from({length: CURRENT_YEAR - 1966 + 1}, (_, i) => String(CURRENT_YEAR - i))

const getMonthYearParts = (value: unknown) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return {year: '', month: ''}
  }

  const [year = '', month = ''] = value.split('-')

  return {year, month}
}

export const MonthYearInput = <T extends FieldValues>({
  name,
  monthUnknownName,
  monthUnknownLabel = "I'm not sure",
  label,
  helperText = '',
  control,
  errors,
  required = false,
  className = '',
  inputClassName = '',
  layout = 'stacked',
  options = {},
}: MonthYearInputProps<T>) => {
  const error = getFieldError(errors, name)
  const watchedValue = useWatch({control, name})
  const watchedParts = React.useMemo(() => getMonthYearParts(watchedValue), [watchedValue])
  const [selectedMonth, setSelectedMonth] = React.useState(watchedParts.month)
  const [selectedYear, setSelectedYear] = React.useState(watchedParts.year)
  const wrapperClassName = layout === 'half' ? 'col-12 col-md-6 mb-0' : 'mb-5'

  React.useEffect(() => {
    setSelectedMonth(watchedParts.month)
    setSelectedYear(watchedParts.year)
  }, [watchedParts.month, watchedParts.year])

  return (
    <div className={`${wrapperClassName} ${className}`.trim()}>
      {label && (
        <label className='form-label fw-semibold' htmlFor={String(name)}>
          {label} {required && <span className='text-danger'>*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={{
          ...options,
          required,
        }}
        render={({field}) => {
          const renderInputs = (
            monthUnknownField?: ControllerRenderProps<T, Path<T>>
          ): React.ReactElement => {
            const supportsUnknownMonth = !!monthUnknownField
            const isMonthUnknown = monthUnknownField?.value === true
            const displayedMonth = !selectedYear
              ? ''
              : supportsUnknownMonth && (isMonthUnknown || !selectedMonth)
              ? UNKNOWN_MONTH_VALUE
              : selectedMonth

            const updateYear = (nextYear: string) => {
              setSelectedYear(nextYear)

              if (!nextYear) {
                setSelectedMonth('')
                field.onChange('')
                monthUnknownField?.onChange(false)
                return
              }

              if (monthUnknownField && (isMonthUnknown || !selectedMonth)) {
                setSelectedMonth('')
                monthUnknownField.onChange(true)
                field.onChange(`${nextYear}-01-01`)
                return
              }

              if (selectedMonth) {
                field.onChange(`${nextYear}-${selectedMonth}-01`)
                return
              }

              field.onChange('')
            }

            const updateMonth = (nextMonth: string) => {
              if (!selectedYear) {
                return
              }

              if (monthUnknownField && nextMonth === UNKNOWN_MONTH_VALUE) {
                setSelectedMonth('')
                monthUnknownField.onChange(true)
                field.onChange(`${selectedYear}-01-01`)
                return
              }

              setSelectedMonth(nextMonth)
              monthUnknownField?.onChange(false)
              field.onChange(nextMonth ? `${selectedYear}-${nextMonth}-01` : '')
            }

            return (
              <div className='row g-2'>
                {/* MONTH */}
                <div className='col-8'>
                  <select
                    className={`form-select ${inputClassName} ${error ? 'is-invalid' : ''}`}
                    value={displayedMonth}
                    disabled={!selectedYear}
                    onBlur={() => {
                      field.onBlur()
                      monthUnknownField?.onBlur()
                    }}
                    onChange={(e) => updateMonth(e.target.value)}
                  >
                    {!selectedYear ? (
                      <option value=''>Select year first</option>
                    ) : supportsUnknownMonth ? (
                      <option value={UNKNOWN_MONTH_VALUE}>{monthUnknownLabel}</option>
                    ) : (
                      <option value=''>Select Month</option>
                    )}

                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* YEAR */}
                <div className='col-4'>
                  <select
                    ref={field.ref}
                    className={`form-select ${inputClassName} ${error ? 'is-invalid' : ''}`}
                    value={selectedYear}
                    onBlur={field.onBlur}
                    onChange={(e) => updateYear(e.target.value)}
                  >
                    <option value=''>Year</option>

                    {YEARS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )
          }

          if (!monthUnknownName) {
            return renderInputs()
          }

          return (
            <Controller
              name={monthUnknownName}
              control={control}
              render={({field: monthUnknownField}) => renderInputs(monthUnknownField)}
            />
          )
        }}
      />

      {helperText && <small className='text-muted d-block mt-1 fs-6'>{helperText}</small>}

      {error && <div className='text-danger mt-1'>{error.message}</div>}
    </div>
  )
}
